import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Dna, RefreshCw, Music, BookOpen, Image, BarChart3,
  Sparkles, Heart, Loader2, Star, Zap, Clock, Archive,
  MessageSquare, Users, Flame
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { pageTransition, fadeUp } from "@/lib/animations";
import { Badge } from "@/components/ui/badge";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, RadarChart, Radar, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis
} from "recharts";
import api from "@/lib/api";
import { toast } from "sonner";

const GOLD = "#00FF88";
const COLORS = ["#00FF88", "#22C55E", "#3B82F6", "#F97316", "#8B5CF6", "#EC4899", "#14B8A6", "#EF4444"];

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#111] border border-white/10 rounded-xl px-3 py-2 shadow-xl">
      <p className="text-xs text-slate-400">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-sm font-medium" style={{ color: p.color || GOLD }}>{p.value}</p>
      ))}
    </div>
  );
}

function TagBadge({ tag, index }) {
  const confidence = tag.confidence || 0.5;
  const opacity = 0.5 + confidence * 0.5;
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.1 * index }}
      className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-[20px] p-5 hover:border-[#00FF88]/20 transition-all group"
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-lg font-bold text-white" style={{ fontFamily: 'Manrope', opacity }}>{tag.tag}</span>
        <span className="text-xs text-[#00FF88] font-mono">{Math.round(confidence * 100)}%</span>
      </div>
      <p className="text-xs text-slate-400 leading-relaxed">{tag.reason}</p>
      <div className="w-full h-1 bg-white/5 rounded-full mt-3 overflow-hidden">
        <div className="h-full bg-[#00FF88] rounded-full" style={{ width: `${confidence * 100}%` }} />
      </div>
    </motion.div>
  );
}

function SentimentMeter({ score, label }) {
  const angle = score * 180;
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-40 h-20 overflow-hidden">
        <svg viewBox="0 0 200 100" className="w-full h-full">
          <path d="M 10 100 A 90 90 0 0 1 190 100" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="12" strokeLinecap="round" />
          <path d="M 10 100 A 90 90 0 0 1 190 100" fill="none" stroke="url(#sentGrad)" strokeWidth="12" strokeLinecap="round"
            strokeDasharray={`${score * 283} 283`} />
          <defs>
            <linearGradient id="sentGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#EF4444" />
              <stop offset="50%" stopColor="#F59E0B" />
              <stop offset="100%" stopColor="#22C55E" />
            </linearGradient>
          </defs>
        </svg>
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-center">
          <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>{Math.round(score * 100)}</p>
        </div>
      </div>
      <p className="text-sm text-[#00FF88] font-medium capitalize mt-2">{label}</p>
    </div>
  );
}

export default function DigitalDNAPage() {
  const [dna, setDna] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadDNA();
  }, []);

  const loadDNA = async () => {
    setLoading(true);
    try {
      const res = await api.get("/digital-dna");
      setDna(res.data);
    } catch (err) {
      toast.error("Failed to load Digital DNA");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await api.post("/digital-dna/refresh");
      setDna(res.data);
      toast.success("Digital DNA refreshed!");
    } catch {
      toast.error("Failed to refresh");
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-12 gap-4">
        <div className="relative">
          <div className="w-16 h-16 border-2 border-[#00FF88]/20 rounded-full animate-pulse" />
          <Dna className="w-8 h-8 text-[#00FF88] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-spin" style={{ animationDuration: '3s' }} />
        </div>
        <p className="text-sm text-slate-400 animate-pulse">Analyzing your digital footprint...</p>
      </div>
    );
  }

  if (!dna) return null;

  const stats = dna.memory_stats || {};
  const writing = dna.writing_style || {};
  const visual = dna.visual_story || {};
  const tags = dna.personality_tags || [];
  const music = dna.music_identity;

  // Prepare chart data
  const vaultPieData = Object.entries(stats.vault_breakdown || {}).map(([name, value]) => ({ name, value }));
  const activityData = Object.entries(stats.activity_by_day || {}).map(([name, value]) => ({ name: name.slice(0, 3), value }));
  const genreData = (music?.top_genres || []).slice(0, 6).map(([name, value]) => ({ genre: name, value }));

  return (
    <motion.div variants={pageTransition} initial="hidden" animate="visible" className="p-6 md:p-10 max-w-[1200px] mx-auto pb-24" data-testid="digital-dna-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-[#00FF88]/10 flex items-center justify-center">
              <Dna className="w-5 h-5 text-[#00FF88]" />
            </div>
            <p className="text-xs tracking-[0.3em] uppercase text-[#00FF88] font-medium">Your Profile</p>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Digital DNA
          </h1>
          {dna.one_liner && (
            <p className="text-base text-slate-300 mt-3 max-w-xl leading-relaxed italic">
              "{dna.one_liner}"
            </p>
          )}
        </div>
        <Button
          onClick={handleRefresh}
          disabled={refreshing}
          data-testid="refresh-dna-btn"
          variant="outline"
          className="border-white/10 text-slate-400 hover:text-[#00FF88] hover:border-[#00FF88]/30 rounded-full"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? "Analyzing..." : "Refresh DNA"}
        </Button>
      </div>

      {/* ─── Section 1: Personality Tags ─── */}
      <section className="mb-10">
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-5 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#00FF88]" /> Personality Tags
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {tags.map((tag, i) => (
            <TagBadge key={tag.tag || i} tag={tag} index={i} />
          ))}
        </div>
      </section>

      {/* ─── Section 2: Writing Style ─── */}
      <section className="mb-10">
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-5 flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-[#00FF88]" /> Writing Style
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Sentiment */}
          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6 flex flex-col items-center justify-center">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-4">Emotional Tone</p>
            <SentimentMeter
              score={writing.sentiment_score || 0.5}
              label={writing.sentiment_label || "balanced"}
            />
          </div>
          {/* Tone & Style */}
          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6 lg:col-span-2">
            <div className="space-y-5">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Tone</p>
                <p className="text-sm text-slate-300 leading-relaxed">{writing.tone || "Still gathering data..."}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Emotional Range</p>
                <p className="text-sm text-slate-300 leading-relaxed">{writing.emotional_range || "N/A"}</p>
              </div>
              <div className="flex items-center gap-3">
                <p className="text-xs text-slate-500 uppercase tracking-wider">Vocabulary</p>
                <Badge variant="outline" className="text-xs border-[#00FF88]/20 text-[#00FF88] capitalize">
                  {writing.vocabulary_level || "conversational"}
                </Badge>
              </div>
              {(writing.topics || []).length > 0 && (
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Key Topics</p>
                  <div className="flex flex-wrap gap-2">
                    {writing.topics.map((topic, i) => (
                      <Badge key={i} variant="outline" className="text-xs border-white/10 text-slate-300 capitalize">
                        {topic}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Section 3: Music Identity ─── */}
      <section className="mb-10">
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-5 flex items-center gap-2">
          <Music className="w-4 h-4 text-[#1DB954]" /> Music Identity
        </h2>
        {music ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Genre Radar */}
            <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-4">Genre Profile</p>
              {genreData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <RadarChart cx="50%" cy="50%" outerRadius="70%" data={genreData}>
                    <PolarGrid stroke="rgba(255,255,255,0.05)" />
                    <PolarAngleAxis dataKey="genre" tick={{ fill: '#64748B', fontSize: 10 }} />
                    <PolarRadiusAxis tick={false} axisLine={false} />
                    <Radar name="Affinity" dataKey="value" stroke="#1DB954" fill="#1DB954" fillOpacity={0.2} strokeWidth={2} />
                  </RadarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-slate-500 text-center py-8">No genre data</p>
              )}
              {dna.music_mood && (
                <p className="text-sm text-slate-400 text-center mt-2 italic">{dna.music_mood}</p>
              )}
            </div>
            {/* Artists & Tracks */}
            <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-4">Favorites</p>
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-[#1DB954] font-medium mb-2">Top Artists</p>
                  <div className="flex flex-wrap gap-2">
                    {(music.top_artists || []).map((a, i) => (
                      <Badge key={i} className="text-xs bg-[#1DB954]/10 text-[#1DB954] border-[#1DB954]/20">{a}</Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-[#1DB954] font-medium mb-2">Top Tracks</p>
                  <div className="space-y-2">
                    {(music.top_tracks || []).map((t, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span className="text-slate-600 w-4">{i + 1}</span>
                        <span className="text-white">{t.name}</span>
                        <span className="text-slate-500">— {t.artist}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex gap-6 pt-2 border-t border-white/[0.06]">
                  <div>
                    <p className="text-xl font-bold text-white">{music.saved_albums_count || 0}</p>
                    <p className="text-xs text-slate-500">Saved Albums</p>
                  </div>
                  <div>
                    <p className="text-xl font-bold text-white">{music.recently_played_count || 0}</p>
                    <p className="text-xs text-slate-500">Recent Plays</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-8 text-center">
            <Music className="w-8 h-8 text-slate-600 mx-auto mb-3" />
            <p className="text-sm text-slate-400">Connect Spotify to reveal your music identity</p>
            <Button
              onClick={() => window.location.href = "/connections/spotify"}
              variant="outline"
              className="border-white/10 text-slate-400 mt-4 rounded-full"
            >
              Connect Spotify
            </Button>
          </div>
        )}
      </section>

      {/* ─── Section 4: Visual Story ─── */}
      <section className="mb-10">
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-5 flex items-center gap-2">
          <Image className="w-4 h-4 text-[#00FF88]" /> Visual Story
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-3">Memory Style</p>
            <p className="text-2xl font-bold text-[#00FF88] capitalize mb-1" style={{ fontFamily: 'Manrope' }}>
              {visual.memory_style || "Collector"}
            </p>
            <p className="text-xs text-slate-400">{visual.photo_personality || "Upload more to discover your style"}</p>
          </div>
          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6 lg:col-span-2">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-3">Dominant Themes</p>
            {(visual.dominant_themes || []).length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {visual.dominant_themes.map((theme, i) => (
                  <Badge
                    key={i}
                    variant="outline"
                    className="text-sm px-4 py-2 rounded-full capitalize"
                    style={{
                      borderColor: `${COLORS[i % COLORS.length]}40`,
                      color: COLORS[i % COLORS.length],
                      background: `${COLORS[i % COLORS.length]}08`,
                    }}
                  >
                    {theme}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500">Upload memories to reveal your visual themes</p>
            )}
          </div>
        </div>
      </section>

      {/* ─── Section 5: Memory Stats ─── */}
      <section className="mb-10">
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-5 flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-[#00FF88]" /> Memory Stats
        </h2>
        {/* Stat Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-5 text-center">
            <Flame className="w-5 h-5 text-[#00FF88] mx-auto mb-2" />
            <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>{stats.total_memories}</p>
            <p className="text-xs text-slate-500">Total Memories</p>
          </div>
          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-5 text-center">
            <Archive className="w-5 h-5 text-slate-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>{stats.vault_items}</p>
            <p className="text-xs text-slate-500">Vault Items</p>
          </div>
          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-5 text-center">
            <Heart className="w-5 h-5 text-red-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>{stats.legacy_messages}</p>
            <p className="text-xs text-slate-500">Legacy Messages</p>
          </div>
          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-5 text-center">
            <Users className="w-5 h-5 text-blue-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>{stats.beneficiaries}</p>
            <p className="text-xs text-slate-500">Beneficiaries</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Content Breakdown */}
          {vaultPieData.length > 0 && (
            <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-4">Content Breakdown</p>
              <div className="flex items-center gap-6">
                <ResponsiveContainer width={140} height={140}>
                  <PieChart>
                    <Pie data={vaultPieData} cx="50%" cy="50%" outerRadius={60} innerRadius={30} paddingAngle={3} dataKey="value">
                      {vaultPieData.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<ChartTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2">
                  {vaultPieData.map((item, i) => (
                    <div key={item.name} className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                      <span className="text-xs text-slate-300 capitalize">{item.name.replace("_", " ")}</span>
                      <span className="text-xs font-bold text-white">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Weekly Activity */}
          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Weekly Activity</p>
            <p className="text-xs text-slate-600 mb-4">Most active: <span className="text-[#00FF88]">{stats.most_active_day}</span></p>
            <ResponsiveContainer width="100%" height={140}>
              <BarChart data={activityData}>
                <XAxis dataKey="name" tick={{ fill: '#64748B', fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis hide allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="value" fill={GOLD} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Footer */}
      <div className="text-center pt-6 border-t border-white/[0.06]">
        <p className="text-xs text-slate-600">
          Generated {dna.generated_at ? new Date(dna.generated_at).toLocaleString() : ""} — Powered by AI
        </p>
      </div>
    </motion.div>
  );
}
